import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  AssetCreated,
  type Asset,
  type AssetKind,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alight/ui";
import {
  Check,
  Copy,
  FileText,
  Film,
  Image as ImageIcon,
  Search,
  Upload,
} from "lucide-react";

/**
 * Asset library — Host Portal.
 *
 * - Grid sourced from GET /api/admin/assets via the shared `apiClient`.
 * - Upload via PUT /api/assets (category + tags + alt).
 * - Copy-URL affordance + "used on" backreferences per asset.
 *
 * The site is served from the same origin as the Worker (Vite proxies `/api`
 * in dev), so relative requests carry the Cloudflare Access cookie. Override the
 * origin with VITE_API_URL when running the SPA against a remote API.
 *
 * Note on uploads: the shared `apiClient.uploadAsset` only forwards
 * filename/category/alt. To honour the brief's `tags` field without editing the
 * shared contract, we issue the PUT directly here while still validating the
 * response with the shared `AssetCreated` schema (no local type duplication).
 */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/** Public R2 origin that fronts asset bytes (mirrors the Worker's R2_PUBLIC_BASE_URL). */
const R2_PUBLIC_BASE =
  (import.meta.env.VITE_R2_PUBLIC_BASE_URL as string | undefined) ??
  "https://assets.alightcreative.com.au";

const ALL = "__all__";

/** Resolve the public URL for an asset's bytes from its R2 key. */
function assetUrl(asset: Asset): string {
  return `${R2_PUBLIC_BASE.replace(/\/$/, "")}/${asset.r2_key}`;
}

function KindIcon({ kind, className }: { kind: AssetKind; className?: string }) {
  if (kind === "image") return <ImageIcon className={className} aria-hidden />;
  if (kind === "video") return <Film className={className} aria-hidden />;
  return <FileText className={className} aria-hidden />;
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function AdminAssets() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();

  const assetsQuery = useQuery({
    queryKey: ["admin", "assets"],
    queryFn: () => api.listAssets(),
    staleTime: 30 * 1000,
  });

  const [search, setSearch] = React.useState("");
  const [category, setCategory] = React.useState<string>(ALL);
  const [kind, setKind] = React.useState<string>(ALL);
  const [copiedId, setCopiedId] = React.useState<number | null>(null);

  const assets = assetsQuery.data ?? [];

  const categories = React.useMemo(() => {
    const set = new Set<string>();
    for (const a of assets) if (a.category) set.add(a.category);
    return [...set].sort((x, y) => x.localeCompare(y));
  }, [assets]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (category !== ALL && a.category !== category) return false;
      if (kind !== ALL && a.kind !== kind) return false;
      if (!q) return true;
      const haystack = [a.filename, a.alt ?? "", a.category ?? "", ...a.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [assets, search, category, kind]);

  const copyUrl = React.useCallback(async (asset: Asset) => {
    const url = assetUrl(asset);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API unavailable (insecure context); fall back to a prompt.
      window.prompt("Copy asset URL", url);
    }
    setCopiedId(asset.id);
    window.setTimeout(() => {
      setCopiedId((cur) => (cur === asset.id ? null : cur));
    }, 1500);
  }, []);

  return (
    <section>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-ink">Assets</h2>
          <p className="mt-1 text-sm text-muted">
            Shared image, video and document library. Upload once, reference anywhere.
          </p>
        </div>
        <UploadDialog
          onUploaded={() => qc.invalidateQueries({ queryKey: ["admin", "assets"] })}
        />
      </header>

      {/* Filter bar */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filename, alt text or tags…"
            className="pl-9"
            aria-label="Search assets"
          />
        </div>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]" aria-label="Filter by category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-[150px]" aria-label="Filter by type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Video</SelectItem>
            <SelectItem value="doc">Documents</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* States */}
      {assetsQuery.isLoading ? (
        <p className="mt-10 text-center text-sm text-muted">Loading assets…</p>
      ) : assetsQuery.isError ? (
        <Card className="mt-6">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted">
              {assetsQuery.error instanceof ApiError
                ? `Couldn’t load assets (${assetsQuery.error.status}).`
                : "Couldn’t load assets."}
            </p>
            <Button variant="outline" size="sm" onClick={() => assetsQuery.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="mt-6">
          <CardContent className="py-12 text-center text-sm text-muted">
            {assets.length === 0
              ? "No assets yet. Upload your first file to get started."
              : "No assets match the current filters."}
          </CardContent>
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              copied={copiedId === asset.id}
              onCopy={() => copyUrl(asset)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function AssetCard({
  asset,
  copied,
  onCopy,
}: {
  asset: Asset;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <Card className="flex flex-col overflow-hidden">
      {/* Preview */}
      <div className="relative flex aspect-[4/3] items-center justify-center bg-paper">
        {asset.kind === "image" ? (
          <img
            src={assetUrl(asset)}
            alt={asset.alt ?? asset.filename}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <KindIcon kind={asset.kind} className="h-10 w-10 text-muted" />
        )}
        <Badge variant="muted" className="absolute left-2 top-2 gap-1">
          <KindIcon kind={asset.kind} className="h-3 w-3" />
          {asset.kind}
        </Badge>
      </div>

      <CardContent className="flex flex-1 flex-col gap-2 p-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink" title={asset.filename}>
            {asset.filename}
          </p>
          <p className="mt-0.5 text-xs text-muted">
            {asset.category ? asset.category : "Uncategorised"}
            {asset.bytes != null ? ` · ${formatBytes(asset.bytes)}` : ""}
            {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ""}
          </p>
        </div>

        {asset.alt ? (
          <p className="line-clamp-2 text-xs text-muted" title={asset.alt}>
            {asset.alt}
          </p>
        ) : null}

        {asset.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {asset.tags.map((t) => (
              <Badge key={t} variant="outline" className="text-[10px]">
                {t}
              </Badge>
            ))}
          </div>
        ) : null}

        {/* Used-on backreferences */}
        <div className="mt-auto">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
            Used on
          </p>
          {asset.used_on.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {asset.used_on.map((ref) => (
                <Badge key={ref} variant="accent" className="text-[10px]">
                  {ref}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted">Not referenced yet</p>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="mt-2 w-full"
          onClick={onCopy}
          aria-label={`Copy URL for ${asset.filename}`}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" /> Copy URL
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

interface UploadDialogProps {
  onUploaded: () => void;
}

function UploadDialog({ onUploaded }: UploadDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [filename, setFilename] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [alt, setAlt] = React.useState("");

  const reset = React.useCallback(() => {
    setFile(null);
    setFilename("");
    setCategory("");
    setTags("");
    setAlt("");
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a file to upload.");
      const name = filename.trim() || file.name;
      const tagList = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      return uploadAsset(file, {
        filename: name,
        category: category.trim() || undefined,
        alt: alt.trim() || undefined,
        tags: tagList,
      });
    },
    onSuccess: () => {
      onUploaded();
      reset();
      setOpen(false);
    },
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    if (picked && !filename.trim()) setFilename(picked.name);
    mutation.reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          reset();
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Upload className="h-4 w-4" /> Upload asset
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload asset</DialogTitle>
          <DialogDescription>
            Add an image, video or document to the shared library.
          </DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="asset-file">File</Label>
            <Input id="asset-file" type="file" onChange={onPick} required />
            {file ? (
              <p className="text-xs text-muted">
                {file.type || "unknown type"} · {formatBytes(file.size)}
              </p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="asset-filename">Filename</Label>
            <Input
              id="asset-filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="e.g. term2-banner.jpg"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="asset-category">Category</Label>
            <Input
              id="asset-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. banners, gallery, forms"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="asset-tags">Tags</Label>
            <Input
              id="asset-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Comma-separated, e.g. term2, hero, kids"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="asset-alt">Alt text</Label>
            <Textarea
              id="asset-alt"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Describe the asset for accessibility and SEO."
              className="min-h-[72px]"
            />
          </div>

          {mutation.isError ? (
            <p className="text-sm font-medium text-accent" role="alert">
              {mutation.error instanceof ApiError
                ? `Upload failed (${mutation.error.status}).`
                : mutation.error instanceof Error
                  ? mutation.error.message
                  : "Upload failed."}
            </p>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!file || mutation.isPending}>
              {mutation.isPending ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PUT /api/assets with the raw file bytes. Mirrors the shared
 * `apiClient.uploadAsset` (filename/category/alt) but additionally forwards
 * `tags`, which the shared client does not yet model. Once the contract gains a
 * `tags` field, prefer `apiClient(API_BASE).uploadAsset` directly. The response
 * is validated with the shared `AssetCreated` schema, so no contract types are
 * duplicated here.
 */
async function uploadAsset(
  file: File,
  meta: { filename: string; category?: string; alt?: string; tags: string[] },
) {
  const params = new URLSearchParams({ filename: meta.filename });
  if (meta.category) params.set("category", meta.category);
  if (meta.alt) params.set("alt", meta.alt);
  for (const tag of meta.tags) params.append("tags", tag);

  // Upload via /api/admin/assets (NOT /api/assets): the admin path carries the
  // portal's Access auth, so a signed-in admin isn't rejected with a 401. The
  // bare /api/assets route is reserved for the Studio sidecar's service uploads.
  const res = await fetch(`${API_BASE.replace(/\/$/, "")}/api/admin/assets?${params.toString()}`, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
    credentials: "same-origin",
  });

  const text = await res.text();
  const json: unknown = text ? safeJson(text) : undefined;
  if (!res.ok) {
    throw new ApiError(res.status, `PUT /api/assets failed (${res.status})`, json);
  }
  return AssetCreated.parse(json);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}
